import React, { useMemo } from 'react';
import targets from './salesDataTargets.json';
import Tabs from './Tabs';
import BudgetChart from './graphs/BudgetChart';
import MonthlyBars from './graphs/MonthlyBars';
import InvoiceStats from './graphs/InvoiceStats';
import TopArticles from './graphs/TopArticles';
import TopCustomers from './graphs/TopCustomers';
import MonitoredArticles from './graphs/MonitoredArticles';
import { MONTHS } from './constants';
import { buildResults, countedOnly, getDrillInvoices, getLastStartedMonth, sum } from './salesCalculations';

const TOTAL_KEY = 'total';

// Wraps to fewer columns only when a card would get narrower than 18rem; never more than `columns` columns
const getGridStyle = (columns) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(max(18rem, calc((100% - ${(columns - 1) * 1.5}rem) / ${columns})), 1fr))`,
    gap: '1.5rem',
    alignItems: 'start',
});

// All graphs for one salesperson (or the total)
const PersonGraphs = ({ entry, lastMonth, year, onShowInvoices }) => {
    // Prefixes the selection title with the salesperson so the list shows which tab it came from
    const showInvoices = (selection) => onShowInvoices({ ...selection, title: `${selection.title} - ${entry.label}` });
    const isTotal = entry.key === TOTAL_KEY;

    // The total has 5 charts and 3 lists in thirds, a salesperson has 4 charts and 1-2 lists in halves
    const gridStyle = getGridStyle(isTotal ? 3 : 2);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div style={gridStyle}>
            <BudgetChart title={entry.label} budget={entry.budget} lastYear={entry.lastYear} result={entry.result} lastMonth={lastMonth} year={year} />
            <MonthlyBars entry={entry} lastMonth={lastMonth} year={year} />
            {/* Web invoices only exist on the total and web tabs, other invoices on the total and salesperson tabs */}
            <InvoiceStats invoices={entry.invoices} lastMonth={lastMonth} showWeb={isTotal || entry.isWeb} showOther={!entry.isWeb} />
        </div>
        {/* The lists get their own row so that their length does not leave whitespace below the charts */}
        <div style={gridStyle}>
            {!entry.isWeb && <TopCustomers invoices={entry.invoices} onShowInvoices={showInvoices} />}
            <TopArticles invoices={entry.invoices} onShowInvoices={showInvoices} />
            {isTotal && <MonitoredArticles />}
        </div>
        </div>
    );
};

const SalesGraphs = ({ buckets, onShowInvoices }) => {
    const results = useMemo(() => buildResults(buckets), [buckets]);
    const lastMonth = getLastStartedMonth();
    const { year } = targets;

    const monthlySum = (pick) => MONTHS.map((_, m) => sum(targets.salespersons.map((p) => pick(p)[m])));

    // One entry per salesperson plus the total, each with one value per month and the invoices behind them
    const entries = [
        ...targets.salespersons.map((p, i) => ({
            key: p.name,
            label: p.name,
            isWeb: Boolean(p.isWeb),
            budget: p.budget,
            lastYear: p.lastYear,
            result: results[i],
            invoices: countedOnly(getDrillInvoices(buckets, { personIndex: i, month: null })),
        })),
        {
            key: TOTAL_KEY,
            label: 'Totalt',
            isWeb: false,
            budget: monthlySum((p) => p.budget),
            lastYear: monthlySum((p) => p.lastYear),
            result: MONTHS.map((_, m) => sum(results.map((r) => r[m]))),
            invoices: countedOnly(getDrillInvoices(buckets, { personIndex: null, month: null })),
        },
    ];

    // Add new graphs to PersonGraphs; every salesperson tab shows the same set
    const tabs = entries.map((entry) => ({
        key: entry.key,
        label: entry.label,
        content: <PersonGraphs entry={entry} lastMonth={lastMonth} year={year} onShowInvoices={onShowInvoices} />,
    }));

    return (
        <div style={{ marginTop: '1.5rem' }}>
            <Tabs tabs={tabs} defaultKey="total" />
        </div>
    );
};

export default SalesGraphs;
